import { useState } from 'react'
import { Send, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/shared/EmptyState'
import { ErrorState } from '@/components/shared/ErrorState'
import {
  useAdminRegulatoryDigestRuns,
  useReplayRegulatoryDigest,
  type AdminRegulatoryDigestRun,
  type DigestRunStatus,
  type DigestRunTrigger,
} from '@/hooks/useAdminRegulatoryDigestRuns'
import { formatDate, formatRelativeDate } from '@/lib/utils'

const STATUS_VARIANT: Record<DigestRunStatus, 'default' | 'secondary' | 'destructive' | 'outline'> =
  {
    completed: 'default',
    partial: 'secondary',
    failed: 'destructive',
    running: 'outline',
  }

const TRIGGER_VARIANT: Record<DigestRunTrigger, 'default' | 'secondary' | 'destructive' | 'outline'> =
  {
    cron: 'secondary',
    manual_replay: 'default',
    test: 'outline',
  }

function statusLabel(status: DigestRunStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function triggerLabel(trigger: DigestRunTrigger): string {
  if (trigger === 'manual_replay') return 'Manual replay'
  if (trigger === 'cron') return 'Cron'
  return 'Test'
}

function durationLabel(run: AdminRegulatoryDigestRun): string {
  if (!run.finishedAt) return 'running…'
  const startMs = new Date(run.startedAt).getTime()
  const endMs = new Date(run.finishedAt).getTime()
  const diffSecs = Math.round((endMs - startMs) / 1000)
  return `${diffSecs}s`
}

export function AdminRegulatoryDigestRunsPage() {
  const { data, isLoading, isError, refetch } = useAdminRegulatoryDigestRuns()
  const replay = useReplayRegulatoryDigest()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [successJobId, setSuccessJobId] = useState<string | null>(null)

  const runs = data?.runs ?? []
  const latestRun = runs[0]
  const latestIsRunning = latestRun?.status === 'running'

  function handleReplayConfirm() {
    replay.mutate(undefined, {
      onSuccess: (result) => {
        setSuccessJobId(result.jobId)
        setConfirmOpen(false)
      },
    })
  }

  const replayErrorMessage =
    replay.error instanceof Error
      ? replay.error.message
      : 'Unable to queue the digest replay. Try again or check the API logs.'

  return (
    <main className="flex flex-col gap-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Mail className="h-5 w-5 text-primary" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Regulatory digest runs</h1>
            <p className="text-sm text-muted-foreground">
              Operator view of weekly digest runs (Mon 08:30 UTC). Last 12 weeks shown.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="default"
          onClick={() => {
            setSuccessJobId(null)
            setConfirmOpen(true)
          }}
          disabled={replay.isPending}
        >
          <Send className="mr-2 h-4 w-4" aria-hidden />
          Replay digest
        </Button>
      </header>

      {successJobId && (
        <div
          role="status"
          className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-primary"
        >
          Digest replay queued — job ID: <span className="font-mono font-semibold">{successJobId}</span>
        </div>
      )}

      {latestIsRunning && (
        <div
          role="status"
          className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800 dark:border-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300"
        >
          Run in progress (started {formatRelativeDate(latestRun.startedAt)})
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState
          title="Failed to load digest runs"
          message="We couldn't reach the admin digest-runs API. Try again in a moment."
          onRetry={() => refetch()}
        />
      ) : runs.length === 0 ? (
        <EmptyState
          icon={Mail}
          title="No digest runs yet"
          description="No digest runs yet — the weekly cron fires Mon 08:30 UTC."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3 font-semibold">Cron fire</th>
                <th className="p-3 font-semibold">Trigger</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold text-right">Candidates</th>
                <th className="p-3 font-semibold text-right">Sent</th>
                <th className="p-3 font-semibold text-right">Skipped (no events)</th>
                <th className="p-3 font-semibold text-right">Skipped (already sent)</th>
                <th className="p-3 font-semibold text-right">Errors</th>
                <th className="p-3 font-semibold text-right">Duration</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id} className="border-b last:border-b-0 hover:bg-muted/20">
                  <td className="p-3">
                    <div className="font-medium">{formatDate(run.cronFire)}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatRelativeDate(run.cronFire)}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge variant={TRIGGER_VARIANT[run.trigger] ?? 'secondary'}>
                      {triggerLabel(run.trigger)}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <Badge variant={STATUS_VARIANT[run.status] ?? 'outline'}>
                      {statusLabel(run.status)}
                    </Badge>
                  </td>
                  <td className="p-3 text-right tabular-nums">{run.candidateUsersCount}</td>
                  <td className="p-3 text-right tabular-nums">{run.emailsSent}</td>
                  <td className="p-3 text-right tabular-nums">{run.usersSkippedNoEvents}</td>
                  <td className="p-3 text-right tabular-nums">{run.usersSkippedAlreadySent}</td>
                  <td className="p-3 text-right tabular-nums">{run.errorsCount}</td>
                  <td className="p-3 text-right tabular-nums text-muted-foreground">
                    {durationLabel(run)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replay regulatory digest?</DialogTitle>
            <DialogDescription>
              This will queue a new digest run with trigger <strong>manual_replay</strong>. Only
              users who have not already received this week's digest will receive an email.
            </DialogDescription>
          </DialogHeader>
          {replay.isError && (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              Replay failed: {replayErrorMessage}
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              onClick={handleReplayConfirm}
              disabled={replay.isPending}
            >
              {replay.isPending ? 'Queuing…' : 'Confirm replay'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  )
}
